/**
 * BrandEditor — Drop-in brand editor component for the @imagia/brand-editor SDK.
 * Self-contained: no react-router, no axios, no Clerk dependencies.
 * Uses the useBrandEditor hook for all state management.
 * Contributor: ben@hapi.vc
 */
import { useEffect } from 'react';
import useBrandEditor from '../hooks/useBrandEditor.js';
import { generateLogoVariants } from '../utils/brandUtils.js';
import ColorPalette from './ColorPalette.jsx';
import FontPreview from './FontPreview.jsx';
import FontPicker from './FontPicker.jsx';
import ProgressStepper from './ProgressStepper.jsx';
import StyleCard from './StyleCard.jsx';
import LogoCard from './LogoCard.jsx';
import LogoPreviewModal from './LogoPreviewModal.jsx';
import TweakModal from './TweakModal.jsx';
import AssetEditorModal from './AssetEditorModal.jsx';
import AssetBrainstormModal from './AssetBrainstormModal.jsx';
import Breadcrumbs from './Breadcrumbs.jsx';

export default function BrandEditor({ client, onBrandApplied, onStepChange, initialStep, showAssetGeneration = true }) {
  const h = useBrandEditor({ client, onStepChange, initialStep });

  // Notify parent when brand is applied (dashboard reached)
  useEffect(() => {
    if (h.step === 'dashboard' && h.brand && h.style) {
      onBrandApplied?.({ brand: h.brand, style: h.style, assets: h.assets });
    }
  }, [h.step, h.brand, h.style]);

  if (h.loading) return <div className="flex items-center justify-center h-96 text-gray-400">Loading brand...</div>;

  return (
    <div className="max-w-5xl mx-auto px-6 py-8">
      {/* Logo Preview Modal */}
      {h.previewSvg && (
        <LogoPreviewModal svg={h.previewSvg} name={h.previewName} brandName={h.brand?.name} onClose={() => h.setPreviewSvg(null)} />
      )}

      {/* Asset Editor Modal */}
      {h.editingAsset && (
        <AssetEditorModal
          asset={h.editingAsset}
          brandStyle={h.style}
          logoVariants={h.getLogoVariants()}
          onClose={() => h.setEditingAsset(null)}
          onSave={async (assetId, newHtml) => {
            try {
              await client.refineAsset(assetId, `Use this exact HTML as the updated asset. Preserve it exactly as-is: ${newHtml.slice(0, 500)}...`);
              h.setEditingAsset(null);
              // Refresh assets
              const assetsResult = await client.getAssets();
              // handled by loadBrand refresh if needed
            } catch (err) {
              h.setError(err.message);
            }
          }}
        />
      )}

      {/* Asset Brainstorm Modal */}
      {h.brainstormingAssetType && (
        <AssetBrainstormModal
          client={client}
          assetType={h.brainstormingAssetType}
          existingAssets={h.generatedAssets.filter(a => a.asset_type === h.brainstormingAssetType.replace(/s$/, ''))}
          onClose={() => h.setBrainstormingAssetType(null)}
          onAssetsGenerated={(updatedAssets) => {
            h.loadBrand();
          }}
        />
      )}

      {/* Tweak Modal */}
      {h.tweakAsset && (
        <TweakModal
          asset={h.tweakAsset}
          onClose={() => h.setTweakAsset(null)}
          onGenerate={h.handleTweakGenerate}
          generating={!!h.generatingMore}
          editMode={h.tweakEditMode}
        />
      )}

      <h1 className="text-2xl font-bold text-gray-900 mb-1">Brand</h1>
      <Breadcrumbs onNavigate={h.setStep} items={
        h.step === 'describe' ? [{ label: 'Create Brand' }] :
        h.step === 'styles' ? [{ label: 'Style Options' }] :
        h.step === 'logos' ? [{ step: 'styles', label: 'Styles' }, { label: 'Logo Options' }] :
        h.step === 'generating' ? [{ label: 'Generating...' }] :
        h.step === 'entry' ? [{ label: 'Get Started' }] :
        []
      } />

      {h.error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-center justify-between">
          <span>{h.error}</span>
          <button onClick={() => h.setError('')} className="text-red-400 hover:text-red-600 ml-3 text-xs">dismiss</button>
        </div>
      )}

      {/* ─── Generating Progress ──────────────────────────── */}
      {h.step === 'generating' && (
        <ProgressStepper steps={h.progressSteps} currentIndex={h.progressIndex} />
      )}

      {/* ─── Entry Screen ─────────────────────────────────── */}
      {h.step === 'entry' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div
            className={`border-2 border-dashed rounded-xl p-8 text-center transition-all ${h.dragOver ? 'border-blue-500 bg-blue-50 scale-[1.02]' : 'border-gray-300 hover:border-gray-400'}`}
            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); h.setDragOver(true); }}
            onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); h.setDragOver(true); }}
            onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); h.setDragOver(false); }}
            onDrop={(e) => {
              e.preventDefault(); e.stopPropagation(); h.setDragOver(false);
              const dropped = Array.from(e.dataTransfer.files);
              if (dropped.length > 0) h.setImportFiles(prev => [...prev, ...dropped]);
            }}
          >
            <div className="text-4xl mb-3">{h.dragOver ? '\uD83D\uDCE5' : '\uD83D\uDCC4'}</div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Import Brand</h3>
            <p className="text-sm text-gray-500 mb-4">
              {h.dragOver ? 'Drop files here' : 'Drag & drop brand guidelines, logos, or documents \u2014 or click to browse'}
            </p>
            <label className="inline-block px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm cursor-pointer hover:bg-gray-200 mb-3">
              Browse Files
              <input type="file" multiple accept=".pdf,.png,.jpg,.jpeg,.txt,.md,.json,.doc,.docx,.rtf"
                onChange={(e) => h.setImportFiles(prev => [...prev, ...Array.from(e.target.files)])}
                className="hidden" />
            </label>
            {h.importFiles.length > 0 && (
              <div className="mt-3 space-y-1">
                {h.importFiles.map((f, i) => (
                  <div key={i} className="flex items-center justify-center gap-2 text-xs text-gray-600">
                    <span className="truncate max-w-[200px]">{f.name}</span>
                    <span className="text-gray-400">({(f.size / 1024).toFixed(0)} KB)</span>
                    <button onClick={(e) => { e.stopPropagation(); h.setImportFiles(prev => prev.filter((_, j) => j !== i)); }} className="text-red-400 hover:text-red-600">x</button>
                  </div>
                ))}
              </div>
            )}
            {h.importFiles.length > 0 && (
              <button onClick={h.handleImport} disabled={h.working} className="mt-3 px-5 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-700 disabled:opacity-40 text-sm">
                {h.working ? 'Extracting...' : `Import ${h.importFiles.length} File${h.importFiles.length > 1 ? 's' : ''}`}
              </button>
            )}
          </div>

          <div className="border-2 border-gray-200 rounded-xl p-8 text-center hover:border-gray-300 transition-colors cursor-pointer" onClick={() => { h.setBrandName(''); h.setStep('describe'); }}>
            <div className="text-4xl mb-3">{'\uD83C\uDFA8'}</div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Create Brand</h3>
            <p className="text-sm text-gray-500 mb-4">Describe your brand and we'll generate logo concepts and style options for you to choose from</p>
            <button className="px-5 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-700 text-sm">Get Started</button>
          </div>
        </div>
      )}

      {/* ─── Describe Step ────────────────────────────────── */}
      {h.step === 'describe' && (
        <div className="max-w-2xl space-y-5">
          <h2 className="text-xl font-bold text-gray-900">Describe Your Brand</h2>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Brand Name</label>
            <input value={h.brandName} onChange={e => h.setBrandName(e.target.value)} className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:border-blue-500" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Industry</label>
              <input value={h.industry} onChange={e => h.setIndustry(e.target.value)} placeholder="e.g. SaaS, Healthcare" className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:border-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Target Audience</label>
              <input value={h.audience} onChange={e => h.setAudience(e.target.value)} placeholder="e.g. Developers, SMBs" className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:border-blue-500" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Describe what you want the brand to look and feel like</label>
            <textarea value={h.description} onChange={e => h.setDescription(e.target.value)} rows={4} placeholder="e.g. Modern and bold, uses dark backgrounds with vibrant accent colors..." className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:border-blue-500 resize-none" />
          </div>
          <div className="flex gap-3">
            <button onClick={() => h.setStep('entry')} className="px-4 py-2.5 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 text-sm">Back</button>
            <button onClick={h.handleDescribe} disabled={!h.brandName.trim() || h.working} className="px-6 py-2.5 bg-gray-900 text-white rounded-xl hover:bg-gray-700 disabled:opacity-40 text-sm font-medium">
              Generate Style Options
            </button>
          </div>
        </div>
      )}

      {/* ─── Style Brainstorm ─────────────────────────────── */}
      {h.step === 'styles' && (
        <div className="space-y-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {h.proposalHistory.length > 0 && (
                <button onClick={h.handleGoBackStyles} className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                  Previous
                </button>
              )}
              <div>
                <h2 className="text-xl font-bold text-gray-900">Choose Your Style</h2>
                <p className="text-sm text-gray-500">Select up to 3 favorites to refine, or pick one to use</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {h.proposalHistory.length > 0 && (
                <span className="text-xs text-gray-400">Round {h.proposalHistory.length + 1}</span>
              )}
              <span className="text-xs text-gray-400">{h.selectedStyles.length}/3 selected</span>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {h.proposals.map((p, i) => (
              <StyleCard key={i} proposal={p} index={i} selected={h.selectedStyles.includes(i)} onToggle={h.toggleStyle} onSelect={h.handleSelectStyle} />
            ))}
          </div>

          {h.selectedStyles.length > 0 && (
            <div className="border border-blue-200 bg-blue-50 rounded-xl p-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Refine your selections — what would you change?</label>
              <textarea value={h.styleFeedback} onChange={e => h.setStyleFeedback(e.target.value)} rows={2} placeholder="e.g. Make the colors warmer, use a more playful font..." className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-blue-500 resize-none mb-3" />
              <button onClick={h.handleRefineStyles} disabled={h.working} className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-500 disabled:opacity-40 text-sm">
                {h.working ? 'Refining...' : `Refine ${h.selectedStyles.length} Selected \u2192 New Options`}
              </button>
            </div>
          )}
        </div>
      )}

      {/* ─── Logo Brainstorm ──────────────────────────────── */}
      {h.step === 'logos' && (
        <div className="space-y-5">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Choose Your Logo</h2>
              <p className="text-sm text-gray-500">Select up to 3 to refine, or pick one to finalize. Star favorites to keep them.</p>
            </div>
            <div className="flex items-center gap-2">
              {h.starredLogos.size > 0 && (
                <button
                  onClick={() => h.setShowStarredOnly(prev => !prev)}
                  className={`text-xs px-3 py-1.5 rounded-lg border transition-colors ${h.showStarredOnly ? 'bg-yellow-50 border-yellow-300 text-yellow-700' : 'border-gray-300 text-gray-500 hover:border-yellow-300'}`}
                >
                  {h.showStarredOnly ? `\u2605 Starred (${h.starredLogos.size})` : `\u2606 ${h.starredLogos.size} starred`}
                </button>
              )}
              {h.hiddenLogos.size > 0 && (
                <button
                  onClick={() => h.setHiddenLogos(new Set())}
                  className="text-xs px-3 py-1.5 rounded-lg border border-gray-300 text-gray-500 hover:border-gray-400"
                >
                  Show {h.hiddenLogos.size} hidden
                </button>
              )}
              <span className="text-xs text-gray-400">{h.selectedLogos.length}/3 selected</span>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {h.logoAssets
              .filter(a => !h.hiddenLogos.has(a.id))
              .filter(a => !h.showStarredOnly || h.starredLogos.has(a.id))
              .map(a => (
              <LogoCard key={a.id} asset={a} selected={h.selectedLogos.includes(a.id)} onToggle={h.toggleLogo} onSelect={h.handleSelectLogo} starred={h.starredLogos.has(a.id)} onStar={h.toggleStarLogo} onMoreLike={h.handleMoreLikeLogo} onEdit={h.handleEditLogo} onDelete={h.handleDeleteLogo} generating={h.generatingMore === a.id} />
            ))}
          </div>

          {h.selectedLogos.length > 0 && (
            <div className="border border-blue-200 bg-blue-50 rounded-xl p-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Describe what you'd like changed</label>
              <textarea value={h.logoFeedback} onChange={e => h.setLogoFeedback(e.target.value)} rows={2} placeholder="e.g. More geometric, simpler lines, use the accent color..." className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-blue-500 resize-none mb-3" />
              <button onClick={h.handleRefineLogos} disabled={h.working || !h.logoFeedback.trim()} className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-500 disabled:opacity-40 text-sm">
                {h.working ? 'Refining...' : `Refine ${h.selectedLogos.length} Selected`}
              </button>
            </div>
          )}

          {h.logoAssets.length === 0 && (
            <div className="text-center py-8">
              <p className="text-gray-500 mb-3">No logos generated yet</p>
              <button onClick={h.handleGenerateLogos} className="px-5 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-700 text-sm">
                Generate Logo Concepts
              </button>
            </div>
          )}
        </div>
      )}

      {/* ─── Dashboard ────────────────────────────────────── */}
      {h.step === 'dashboard' && h.brand && (
        <div className="space-y-6">
          <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3">
            <span className="text-green-600 text-lg">&#10003;</span>
            <div>
              <div className="text-sm font-medium text-green-800">Brand applied to project</div>
              <div className="text-xs text-green-600">Your project uses the {h.brand.name} brand identity</div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Logo */}
            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Logo</h3>
              {(() => {
                const logoAsset = h.getApprovedLogo();
                const src = logoAsset?.thumbnail_url || logoAsset?.file_url;
                const svgContent = logoAsset?.content;
                const hasSvg = svgContent?.startsWith('<svg');

                return (
                  <div>
                    <div className="flex items-center justify-center min-h-[100px] mb-3 cursor-pointer" onClick={() => { if (hasSvg) { h.setPreviewSvg(svgContent); h.setPreviewName('Logo'); } }}>
                      {src ? <img src={src} alt="Logo" className="max-h-32 mx-auto" /> :
                       hasSvg ? <div dangerouslySetInnerHTML={{ __html: svgContent }} className="max-h-32 mx-auto [&>svg]:max-h-32 [&>svg]:mx-auto" /> :
                       <span className="text-gray-300">No logo</span>}
                    </div>
                    {(src || hasSvg) && (
                      <div className="flex gap-2 justify-center">
                        {hasSvg && (
                          <button onClick={() => {
                            const blob = new Blob([svgContent], { type: 'image/svg+xml' });
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement('a'); a.href = url; a.download = `${h.brand?.name || 'logo'}.svg`; a.click();
                            URL.revokeObjectURL(url);
                          }} className="text-[10px] px-3 py-1 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">
                            Download SVG
                          </button>
                        )}
                        {hasSvg && (
                          <button onClick={() => {
                            const canvas = document.createElement('canvas');
                            canvas.width = 1024; canvas.height = 1024;
                            const ctx = canvas.getContext('2d');
                            const img = new Image();
                            img.onload = () => {
                              ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, 1024, 1024);
                              const scale = Math.min(900 / img.naturalWidth, 900 / img.naturalHeight);
                              const w = img.naturalWidth * scale, hh = img.naturalHeight * scale;
                              ctx.drawImage(img, (1024 - w) / 2, (1024 - hh) / 2, w, hh);
                              canvas.toBlob(blob => {
                                const url = URL.createObjectURL(blob);
                                const a = document.createElement('a'); a.href = url; a.download = `${h.brand?.name || 'logo'}.png`; a.click();
                                URL.revokeObjectURL(url);
                              }, 'image/png');
                            };
                            img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgContent)));
                          }} className="text-[10px] px-3 py-1 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">
                            Download PNG
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })()}
              <button onClick={() => { h.setStep('logos'); }} className="mt-3 w-full text-xs py-1.5 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50">Edit Logo</button>
            </div>

            {/* Colors */}
            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Colors</h3>
              {h.style && <ColorPalette showLabels colors={h.getStyleColors()} />}
              <button onClick={h.handleChangeStyle} className="mt-3 w-full text-xs py-1.5 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50">Change Style</button>
            </div>

            {/* Typography */}
            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Typography</h3>
              {h.style && <FontPreview heading={h.style.font_heading} body={h.style.font_body} />}
              <button onClick={() => { h.setEditHeadingFont(h.style?.font_heading || 'Inter'); h.setEditBodyFont(h.style?.font_body || 'Inter'); h.setEditingTypography(true); }} className="mt-3 w-full text-xs py-1.5 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50">Change Typography</button>
            </div>
          </div>

          {/* Brand info */}
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
              <div><span className="text-gray-500 block text-xs">Brand</span>{h.brand.name}</div>
              <div><span className="text-gray-500 block text-xs">Industry</span>{h.brand.industry || '\u2014'}</div>
              <div><span className="text-gray-500 block text-xs">Audience</span>{h.brand.target_audience || '\u2014'}</div>
              <div><span className="text-gray-500 block text-xs">Status</span><span className="text-green-600">{h.brand.status}</span></div>
            </div>
          </div>

          {/* ─── Asset Generation ──────────────────────── */}
          {showAssetGeneration && (
            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4">Brand Assets</h3>

              {/* Active jobs */}
              {h.activeJobs.length > 0 && (
                <div className="mb-4 space-y-2">
                  {h.activeJobs.map(job => (
                    <div key={job.id} className="flex items-center gap-3 p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
                      <div className="w-4 h-4 animate-spin rounded-full border-2 border-indigo-200 border-t-indigo-600 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium text-indigo-800">{job.type?.replace(/_/g, ' ')} \u2014 {job.currentStep || job.status}</div>
                        <div className="w-full bg-indigo-200 rounded-full h-1 mt-1">
                          <div className="bg-indigo-600 h-1 rounded-full transition-all duration-500" style={{ width: `${job.progress || 2}%` }} />
                        </div>
                      </div>
                      <span className="text-[10px] text-indigo-400">{job.progress}%</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Presentation topic */}
              <div className="flex gap-2 mb-4">
                <input value={h.presentationTopic} onChange={e => h.setPresentationTopic(e.target.value)} placeholder="Presentation topic (e.g. Product Overview, Investor Pitch)" className="flex-1 text-sm px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500" />
                <button onClick={() => h.handleGenerateAsset('presentation', { presentation: { topic: h.presentationTopic } })}
                  disabled={!h.presentationTopic.trim() || !!h.generatingAssetType}
                  className="px-4 py-2 bg-gray-900 text-white rounded-lg text-sm hover:bg-gray-700 disabled:opacity-40 flex-shrink-0">
                  {h.generatingAssetType === 'presentation' ? 'Generating...' : 'Generate Slides'}
                </button>
              </div>

              {/* Logo variants */}
              {(() => {
                const variants = h.getLogoVariants();
                if (variants.length === 0) return null;
                return (
                  <div className="border-t border-gray-100 pt-4">
                    <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Logo Variants <span className="text-gray-400 font-normal">({variants.length})</span></h4>
                    <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
                      {variants.map(v => (
                        <div key={v.key} className="border border-gray-200 rounded-lg overflow-hidden bg-white hover:shadow-md transition-shadow cursor-pointer" onClick={() => { h.setPreviewSvg(v.svg); h.setPreviewName(v.name); }}>
                          <div className={`aspect-square flex items-center justify-center p-2 ${v.key === 'dark' || v.key === 'brand-bg' ? '' : 'bg-gray-50'}`}
                            style={v.key === 'transparent' ? { backgroundImage: 'linear-gradient(45deg, #eee 25%, transparent 25%), linear-gradient(-45deg, #eee 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #eee 75%), linear-gradient(-45deg, transparent 75%, #eee 75%)', backgroundSize: '16px 16px', backgroundPosition: '0 0, 0 8px, 8px -8px, -8px 0' } : {}}>
                            <div dangerouslySetInnerHTML={{ __html: v.svg }} className="w-full h-full [&>svg]:w-full [&>svg]:h-full" />
                          </div>
                          <div className="p-1.5 flex items-center justify-between">
                            <span className="text-[9px] text-gray-500 truncate">{v.name}</span>
                            <button onClick={(e) => {
                              e.stopPropagation();
                              const blob = new Blob([v.svg], { type: 'image/svg+xml' });
                              const url = URL.createObjectURL(blob);
                              const a = document.createElement('a'); a.href = url; a.download = `${h.brand?.name || 'logo'}-${v.key}.svg`; a.click();
                              URL.revokeObjectURL(url);
                            }} className="text-[8px] px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded hover:bg-gray-200">SVG</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* Asset brainstorm sections */}
              {(() => {
                const ASSET_SECTIONS = [
                  { type: 'email_template', genType: 'email_templates', label: 'Email Templates', icon: '\uD83D\uDCE7', desc: 'Welcome, Newsletter, Promo, Announcement' },
                  { type: 'social_image', genType: 'social_images', label: 'Social Media', icon: '\uD83D\uDCF1', desc: 'Instagram, Twitter, LinkedIn, Facebook' },
                  { type: 'page_layout', genType: 'page_layouts', label: 'Page Layouts', icon: '\uD83D\uDDA5', desc: 'Hero, Features, Pricing, About' },
                  { type: 'icon_set', genType: 'icon_set', label: 'Icons', icon: '\u2B21', desc: 'App icons, favicons, OG images' },
                  { type: 'presentation', genType: 'presentation', label: 'Presentations', icon: '\uD83D\uDCCA', desc: 'Slide decks' },
                ];
                const grouped = h.generatedAssets.reduce((acc, a) => { (acc[a.asset_type] = acc[a.asset_type] || []).push(a); return acc; }, {});

                return (
                  <div className="space-y-6 border-t border-gray-100 pt-4">
                    {ASSET_SECTIONS.map(({ type, genType, label, icon, desc }) => {
                      const items = grouped[type] || [];
                      const isGenerating = h.generatingAssetType === genType || h.activeJobs.some(j => j.type === genType);
                      return (
                        <div key={type}>
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wide flex items-center gap-1.5">
                              <span>{icon}</span> {label}
                              {items.length > 0 && <span className="text-gray-400 font-normal">({items.length})</span>}
                            </h4>
                            <button onClick={() => {
                              if (genType === 'icon_set') {
                                const approved = h.logoAssets.find(a => a.status === 'approved');
                                if (!approved) { h.setError('Approve a logo first'); return; }
                                h.handleGenerateAsset(genType, { icon_set: { logo_asset_id: approved.id } });
                              } else {
                                h.setBrainstormingAssetType(genType);
                              }
                            }} disabled={isGenerating}
                              className="text-[10px] px-3 py-1 bg-gray-900 text-white rounded-lg hover:bg-gray-700 disabled:opacity-40 flex items-center gap-1.5">
                              {isGenerating ? (
                                <><span className="w-3 h-3 animate-spin rounded-full border border-white/30 border-t-white" /> Generating...</>
                              ) : items.length > 0 ? 'Generate More' : `Generate ${label}`}
                            </button>
                          </div>
                          {items.length === 0 && !isGenerating && (
                            <div className="text-center py-6 border border-dashed border-gray-200 rounded-lg text-gray-400 text-xs">
                              No {label.toLowerCase()} yet \u2014 click Generate to explore styles
                            </div>
                          )}
                          {items.length > 0 && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                              {(h.showStarredAssets ? items.filter(a => h.starredAssets.has(a.id)) : items).map(asset => {
                                const src = asset.thumbnail_url || asset.file_url;
                                const isHtml = asset.file_format === 'html' || asset.content?.includes('<');
                                const isStarred = h.starredAssets.has(asset.id);
                                return (
                                  <div key={asset.id} className="border border-gray-200 rounded-lg overflow-hidden bg-white hover:shadow-md transition-shadow relative">
                                    <div className="aspect-video bg-white relative overflow-hidden cursor-pointer" onClick={() => { if (isHtml && asset.content) h.setEditingAsset(asset); }}>
                                      {src && !isHtml ? <img src={src} alt={asset.subtype} className="w-full h-full object-cover" /> :
                                       isHtml && asset.content ? (
                                        <iframe srcDoc={asset.content} className="absolute inset-0 border-0 pointer-events-none origin-top-left" style={{ width: '500%', height: '500%', transform: 'scale(0.2)' }} title={asset.subtype} sandbox="allow-same-origin allow-scripts" />
                                       ) : <span className="text-gray-300 text-xs">{asset.subtype?.replace(/_/g, ' ')}</span>}
                                      <button onClick={(e) => { e.stopPropagation(); h.setStarredAssets(prev => { const n = new Set(prev); if (n.has(asset.id)) n.delete(asset.id); else n.add(asset.id); return n; }); }}
                                        className={`absolute top-1.5 left-1.5 text-base leading-none z-10 drop-shadow ${isStarred ? 'text-yellow-400' : 'text-white/70 hover:text-yellow-300'}`}>
                                        {isStarred ? '\u2605' : '\u2606'}
                                      </button>
                                    </div>
                                    <div className="p-1.5 flex items-center gap-1">
                                      <span className="text-[9px] text-gray-500 truncate flex-1">{asset.subtype?.replace(/_/g, ' ')}</span>
                                      {isHtml && asset.content && (
                                        <button onClick={() => h.setEditingAsset(asset)} className="text-[8px] px-2 py-0.5 bg-gray-900 text-white rounded hover:bg-gray-700">Edit</button>
                                      )}
                                      <button onClick={async () => {
                                        h.setError('');
                                        try {
                                          const result = await client.refineAsset(asset.id, `Generate a new variation of this ${asset.asset_type.replace(/_/g, ' ')} with a different layout and design approach but same brand. Output only clean HTML.`);
                                          const newAsset = result.asset || result;
                                          if (newAsset?.id) h.loadBrand();
                                        } catch {}
                                      }} disabled={!!h.generatingAssetType} className="text-[8px] px-2 py-0.5 border border-gray-200 text-gray-500 rounded hover:bg-gray-50">
                                        More
                                      </button>
                                      <button onClick={async () => {
                                        try { await client.rejectAsset(asset.id); } catch {}
                                        h.loadBrand();
                                      }} className="text-[8px] px-1 py-0.5 text-red-400 hover:text-red-600">
                                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                      </button>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          )}

          {/* Remove brand */}
          <div className="flex justify-end">
            <button onClick={h.handleRemoveBrand} disabled={h.working} className="px-4 py-2 border border-red-200 text-red-600 rounded-lg text-sm hover:bg-red-50">Remove Brand</button>
          </div>

          {/* Typography editor */}
          {h.editingTypography && (
            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Change Typography</h3>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Heading Font</label>
                  <FontPicker value={h.editHeadingFont} onChange={h.setEditHeadingFont} />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Body Font</label>
                  <FontPicker value={h.editBodyFont} onChange={h.setEditBodyFont} />
                </div>
              </div>
              <FontPreview heading={h.editHeadingFont} body={h.editBodyFont} />
              <div className="flex gap-2 mt-4">
                <button onClick={h.handleSaveTypography} disabled={h.savingTypography} className="px-4 py-2 bg-gray-900 text-white rounded-lg text-sm hover:bg-gray-700 disabled:opacity-40">
                  {h.savingTypography ? 'Saving...' : 'Save Typography'}
                </button>
                <button onClick={() => h.setEditingTypography(false)} className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
